import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { Button } from "@/components/ui/button";
import { WIDGETS, SPAN_CLASS, DASHBOARD_SECTIONS } from "@/components/DashboardWidgets";
import {
  DashboardSwitcher,
  PublishedDashboardView,
  usePublishedDashboards,
} from "@/components/dashboards/PublishedDashboards";
import { Gauge, RefreshCw, AlertCircle } from "lucide-react";

const PERIODS = [
  { v: "0", l: "Todo o período" },
  { v: "30", l: "Últimos 30 dias" },
  { v: "90", l: "Últimos 90 dias" },
  { v: "180", l: "Últimos 180 dias" },
  { v: "365", l: "Últimos 12 meses" },
];

function normalizeId(value) {
  return value === null || value === undefined ? "" : String(value).trim();
}

/**
 * Dashboard: painel publicado no Design do Dashboard (quando escolhido ou
 * marcado como padrão para o perfil) ou a visão padrão ASTRAL.
 */
export default function Dashboard() {
  const published = usePublishedDashboards();
  const switcher = <DashboardSwitcher {...published} />;
  if (published.selectedId) {
    return <PublishedDashboardView dashboardId={published.selectedId} switcher={switcher} canDesign={published.canDesign} />;
  }
  return <StandardDashboard switcher={switcher} />;
}

function StandardDashboard({ switcher }) {
  const { user } = useAuth();
  const {
    clients: contextClients,
    selectedClientIds,
    isAll,
    loading: clientLoading,
  } = useClient();

  // =============================================================
  // CONTEXTO GLOBAL DE CLIENTE — fonte única para o Dashboard
  // =============================================================
  const effectiveClientIds = useMemo(() => {
    if (isAll) {
      return contextClients.map((c) => normalizeId(c.id));
    }
    return selectedClientIds.map(normalizeId).filter(Boolean);
  }, [selectedClientIds, isAll, contextClients]);

  const hasClientSelection = effectiveClientIds.length > 0;

  const buildParams = (extra = {}) => {
    const params = { ...extra };
    if (isAll) {
      params.is_all = "true";
    } else if (effectiveClientIds.length === 1) {
      params.client_id = effectiveClientIds[0];
    } else if (effectiveClientIds.length > 1) {
      params.client_ids = effectiveClientIds.join(",");
    }
    return params;
  };

  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [bi, setBi] = useState(null);
  const [vis, setVis] = useState(null);
  const [layout, setLayout] = useState(null);
  const [days, setDays] = useState("0");

  const [loadingBase, setLoadingBase] = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState("");
  const [reloadTick, setReloadTick] = useState(0);

  // Carrega visibilidade e layout global (apenas apresentação)
  useEffect(() => {
    let cancelled = false;
    async function loadVisibilityAndLayout() {
      setLoadingBase(true);
      try {
        const [visibilityRes, layoutRes] = await Promise.all([
          api.get("/dashboard/visibility"),
          api.get("/dashboard/layout").catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setVis(visibilityRes?.data || {});
          setLayout(layoutRes?.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("DASHBOARD BASE ERROR", err);
          setError("Não foi possível carregar as configurações do Dashboard.");
          setVis({});
        }
      } finally {
        if (!cancelled) setLoadingBase(false);
      }
    }
    loadVisibilityAndLayout();
    return () => { cancelled = true; };
  }, []);

  // Carrega dados — reage a mudança de contexto global
  useEffect(() => {
    if (!hasClientSelection) {
      setStats(null);
      setCharts(null);
      setBi(null);
      setLoadingData(false);
      return;
    }

    let cancelled = false;
    async function loadDashboard() {
      setLoadingData(true);
      setError("");
      const params = buildParams({ days });
      try {
        const results = await Promise.allSettled([
          api.get("/dashboard/stats", { params }),
          api.get("/dashboard/charts", { params }),
          api.get("/dashboard/bi", { params }),
        ]);
        if (cancelled) return;
        const [s, c, b] = results;
        if (s.status === "fulfilled") setStats(s.value.data);
        if (c.status === "fulfilled") setCharts(c.value.data);
        if (b.status === "fulfilled") setBi(b.value.data);
        const failed = results.filter((r) => r.status === "rejected");
        if (failed.length === results.length) {
          setError("Não foi possível carregar os dados do Dashboard.");
        } else if (failed.length > 0) {
          setError("Alguns indicadores não puderam ser atualizados.");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("DASHBOARD LOAD ERROR", err);
          setError("Erro ao atualizar o Dashboard.");
        }
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }
    loadDashboard();
    return () => { cancelled = true; };
  }, [effectiveClientIds, isAll, days, reloadTick]);

  const layoutCfg = layout?.layout || null;
  const widgetCfg = (layoutCfg && layoutCfg.widgets) || {};
  const layoutMode = (layoutCfg && layoutCfg.mode) || "sections";

  const [activeSectionTabRaw, setActiveSectionTab] = useState(null);
  const gridCols = (layoutCfg && layoutCfg.columns) || 3;
  const styleCfg = (layoutCfg && layoutCfg.style) || {};

  const effectiveSections = useMemo(() => {
    if (!layoutCfg || !Array.isArray(layoutCfg.sections) || layoutCfg.sections.length === 0) {
      return DASHBOARD_SECTIONS;
    }
    return layoutCfg.sections
      .filter((s) => s && s.enabled !== false)
      .map((s) => ({ ...s }))
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
  }, [layoutCfg]);

  const activeSectionTab = effectiveSections.some((s) => s.id === activeSectionTabRaw)
    ? activeSectionTabRaw
    : effectiveSections[0]?.id;

  const visible = useMemo(
    () =>
      WIDGETS.filter((w) => {
        if (vis && vis[w.vis] === false) return false;
        const cfg = widgetCfg[w.id];
        if (cfg && cfg.enabled === false) return false;
        return true;
      }),
    [vis, widgetCfg],
  );

  const grouped = useMemo(() => {
    const groups = {};
    for (const section of effectiveSections) {
      const order = Array.isArray(section.widget_order) ? section.widget_order : null;
      const members = visible.filter((w) => w.section === section.id);
      members.sort((a, b) => {
        const cfgA = widgetCfg[a.id];
        const cfgB = widgetCfg[b.id];
        const idxA = cfgA?.order ?? (order ? order.indexOf(a.id) : -1);
        const idxB = cfgB?.order ?? (order ? order.indexOf(b.id) : -1);
        const rankA = idxA >= 0 ? idxA : Number.MAX_SAFE_INTEGER;
        const rankB = idxB >= 0 ? idxB : Number.MAX_SAFE_INTEGER;
        return rankA - rankB;
      });
      groups[section.id] = members;
    }
    return groups;
  }, [visible, effectiveSections, widgetCfg]);

  // Time Lapse
  const defaultPeriodApplied = useRef(false);
  useEffect(() => {
    if (defaultPeriodApplied.current) return;
    const tl = layout?.time_lapse;
    if (!tl || !tl.default_period) return;
    const map = { "0": "0", "7d": "7", "30d": "30", "90d": "90", "180d": "180", "365d": "365" };
    const daysValue = map[tl.default_period];
    if (daysValue != null) {
      setDays(daysValue);
      defaultPeriodApplied.current = true;
    }
  }, [layout]);

  const gridColsClass = { 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4" }[gridCols] || "lg:grid-cols-3";
  const densityGapClass = styleCfg.density === "compact" ? "gap-2 sm:gap-3" : "gap-4 sm:gap-6";
  const sectionTitleClass = styleCfg.section_title_size === "sm" ? "text-base" : styleCfg.section_title_size === "lg" ? "text-xl" : "text-lg";
  const sectionCardClass = ["rounded-lg border border-border/70 bg-card", styleCfg.card_shadow ? "shadow-md" : ""].filter(Boolean).join(" ");

  if (loadingBase && !vis) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const data = {
    stats,
    charts,
    bi,
    filters: {
      clientId: effectiveClientIds[0] || null,
      clientIds: effectiveClientIds,
      isAll,
      days,
    },
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Dashboard Inteligente
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Indicadores de BI operacional, controle de pragas, roedores,
            não conformidades e inteligência ambiental.
          </p>

          {!isAll && (
            <p className="text-[11px] text-muted-foreground mt-2">
              Contexto:{" "}
              {hasClientSelection
                ? `${effectiveClientIds.length} cliente(s)`
                : "nenhum cliente selecionado"}{" "}
              ·{" "}
              <span className={hasClientSelection ? "text-green-400" : "text-amber-400"}>
                {hasClientSelection ? "Dados atualizados" : "Selecione um cliente no header"}
              </span>
            </p>
          )}

          {isAll && (
            <p className="text-[11px] text-muted-foreground mt-2">
              Contexto: Todos os clientes do seu membership
              {" "}·{" "}
              <span className="text-green-400">Dados consolidados</span>
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {!isAll && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground uppercase">Período</span>
              <select
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="h-9 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="dashboard-period-filter"
              >
                {PERIODS.map((p) => (
                  <option key={p.v} value={p.v}>{p.l}</option>
                ))}
              </select>
            </div>
          )}

          {switcher}

          <Button variant="outline" asChild data-testid="open-cockpit-btn">
            <Link to="/cockpit">
              <Gauge size={15} className="mr-2" />
              Cockpit 360
            </Link>
          </Button>

          {/*
            NOTA: O botão "Configurar Dashboard" foi REMOVIDO da tela
            operacional. Painéis personalizados são montados no Design do
            Dashboard (/admin/dashboards) e escolhidos no seletor "Painel".
          */}
        </div>
      </div>

      {error && (
        <div
          className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-center gap-3"
          data-testid="dashboard-load-warning"
        >
          <AlertCircle size={17} className="text-amber-400 shrink-0" />
          <p className="text-sm text-amber-200 flex-1">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setReloadTick((c) => c + 1)}
          >
            <RefreshCw size={14} className="mr-2" />
            Atualizar
          </Button>
        </div>
      )}

      {loadingData && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <div className="h-3.5 w-3.5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          Atualizando indicadores...
        </div>
      )}

      {!hasClientSelection && !loadingData && (
        <div className="rounded-xl border border-border/50 bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Nenhum cliente selecionado. Use o seletor de cliente no header
            para visualizar dados do Dashboard.
          </p>
        </div>
      )}

      {layoutMode === "tabs" ? (
        <div className="space-y-4" data-testid="dashboard-tabs-mode">
          <div className="flex flex-wrap gap-1 border-b border-border pb-2">
            {effectiveSections.map((section) => {
              const widgets = grouped[section.id] || [];
              if (!widgets.length) return null;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSectionTab(section.id)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeSectionTab === section.id
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent"
                  }`}
                  data-testid={`dashboard-tab-${section.id}`}
                >
                  {section.title}
                </button>
              );
            })}
          </div>

          {effectiveSections
            .filter((section) => section.id === activeSectionTab)
            .map((section) => {
              const widgets = grouped[section.id] || [];
              if (!widgets.length) return null;
              return (
                <div key={section.id}>
                  <div className="border-b border-border/70 pb-3 mb-4">
                    <h2 className={`font-heading font-semibold tracking-tight ${sectionTitleClass}`}>{section.title}</h2>
                    {section.description && (
                      <p className="text-xs text-muted-foreground mt-1">{section.description}</p>
                    )}
                  </div>
                  <div className={`grid grid-cols-1 ${gridColsClass} ${densityGapClass}`}>
                    {widgets.map(({ id, span, C }) => (
                      <div key={id} className={SPAN_CLASS[widgetCfg[id]?.span ?? span]}>
                        <C
                          {...data}
                          filters={{
                            clientId: effectiveClientIds[0] || null,
                            clientIds: effectiveClientIds,
                            isAll,
                            days,
                          }}
                          config={widgetCfg[id]}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        effectiveSections.map((section) => {
          const widgets = grouped[section.id] || [];
          if (!widgets.length) return null;
          return (
            <section key={section.id} className="space-y-4" data-testid={`dashboard-section-${section.id}`}>
              <div className={`${sectionCardClass} pb-3 px-4 pt-3`}>
                <h2 className={`font-heading font-semibold tracking-tight ${sectionTitleClass}`}>{section.title}</h2>
                {section.description && (
                  <p className="text-xs text-muted-foreground mt-1">{section.description}</p>
                )}
              </div>
              <div className={`grid grid-cols-1 ${gridColsClass} ${densityGapClass}`}>
                {widgets.map(({ id, span, C }) => (
                  <div key={id} className={SPAN_CLASS[widgetCfg[id]?.span ?? span]}>
                    <C
                      {...data}
                      filters={{
                        clientId: effectiveClientIds[0] || null,
                        clientIds: effectiveClientIds,
                        isAll,
                        days,
                      }}
                      config={widgetCfg[id]}
                    />
                  </div>
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
